import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Box } from '@mui/material';
import Tab from '@mui/material/Tab';
import { FormErrors } from '@qfa/forms';
import { DetailsButton } from 'components/buttons';
import CardPage from 'components/layouts/CardPage';
import PageHeader, { PageHeaderProps } from 'components/layouts/PageHeader';
import RouteLeavingGuard from 'components/RouteLeavingGuard';
import { LOCATIONS_PAGE_ROUTE } from 'const/routes';
import { useRouteBuilder } from 'kineticUtils/hooks/useRouteBuilder';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FormMode } from 'types/form';
import { PopulateFromTemplateButton } from './components/PopulateFromTemplateButton';
import { WorkflowButton } from './components/WorkflowButton';
import { Configuration } from './Configuration/Configuration';
import { LOCATION_CONFIG_PATH, LOCATION_DETAILS_PATH } from './const';
import { LocationFormContext } from './context';
import { useIsGeometricConfigEnabled } from './hooks/useIsGeometricConfigEnabled';
import { useLocationForm } from './hooks/useLocationForm';
import { DETAILS } from './LocationDetails/const';
import LocationDetails from './LocationDetails/LocationDetails';
import useCRUDStyles, { useResponsiveStyles } from './styles';
import { LocationFormDirtyState, LocationFormErrorsState, LocationFormProps } from './types';

const LocationForm: React.FunctionComponent<LocationFormProps> = ({
  initialData,
  hasPrivilege,
  headerProps,
  tabPath,
  mode = FormMode.CREATE,
  useForm = useLocationForm,
  showWorkflowButton = true,
  showPopulateButton = true,
  showBackButton = true,
  locationParams,
}) => {
  const { t } = useTranslation(['locations', 'common']);
  const classes = useCRUDStyles();
  const breakpointRef = useRef(null);
  const responsiveClasses = useResponsiveStyles(breakpointRef);
  const listRoute = useRouteBuilder(LOCATIONS_PAGE_ROUTE);

  const {
    saveButtonProps: { onSaveClick },
    headerProps: formHeaderProps,
    valuesProps: { setValues, values },
  } = useForm(initialData, hasPrivilege);

  const { geometricConfigEnabled, getGeometricConfigEnabled } = useIsGeometricConfigEnabled(
    values?.locationType
  );

  const [dirty, setDirty] = useState<LocationFormDirtyState>({});
  const [errors, setErrors] = useState<LocationFormErrorsState>({});

  const [saving, setSaving] = useState(false);

  // Enables save button when cloning
  useEffect(() => {
    if (mode === FormMode.CLONE) {
      setDirty({ [DETAILS]: true });
    }
  }, [mode, setDirty]);

  const isDirty = !!Object.values(dirty).find((state) => state);
  const isErrors = !!Object.values(errors).find(
    (error) => !!Object.values(error).find((error) => !!error)
  );

  const setContextErrors = useCallback((id: string, errors: FormErrors<any>) => {
    setErrors((prev) => {
      return {
        ...prev,
        [id]: errors,
      };
    });
  }, []);

  const setContextDirty = useCallback((id: string) => {
    setDirty((prev) => {
      return {
        ...prev,
        [id]: true,
      };
    });
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);

    try {
      await onSaveClick();
      setDirty({});
      setErrors({});
    } finally {
      setSaving(false);
    }
  }, [onSaveClick]);

  const pageHeaderProps: PageHeaderProps = useMemo(() => {
    const headerButtons = (
      <>
        <PageHeader.SaveButton
          onClick={handleSave}
          disabled={!hasPrivilege || !isDirty || isErrors || saving}
        />
        {showWorkflowButton && (
          <WorkflowButton
            locationId={values?.id ?? ''}
            locationType={values?.locationType}
            disabled={!hasPrivilege || isErrors || saving}
          />
        )}
        {initialData?.id && (
          <DetailsButton title={t('common:goToDetails')} to={listRoute(initialData.id)} />
        )}
        {!initialData?.id && showPopulateButton && (
          <PopulateFromTemplateButton setFormValues={setValues} />
        )}
      </>
    );
    return {
      backButton: showBackButton ? { title: t('locations'), to: listRoute() } : undefined,
      addonsRight: headerButtons,
      document: 'LOCATION_EDIT',
      ...formHeaderProps,
      ...headerProps,
    };
  }, [
    handleSave,
    hasPrivilege,
    isDirty,
    isErrors,
    saving,
    showWorkflowButton,
    values?.id,
    values?.locationType,
    initialData.id,
    t,
    listRoute,
    showPopulateButton,
    setValues,
    showBackButton,
    formHeaderProps,
    headerProps,
  ]);

  const { tab = LOCATION_DETAILS_PATH } = locationParams;

  const isConfigTabDisabled = !values?.geometry || !geometricConfigEnabled;

  return (
    <TabContext value={tab}>
      <CardPage
        headerProps={pageHeaderProps}
        cardHeader={
          <div className={classes.tabs} ref={breakpointRef}>
            <Box
              sx={{
                flexGrow: 1,
                bgcolor: 'background.paper',
              }}
              className={responsiveClasses.tabsWrapper}
            >
              <TabList
                sx={{ overflow: 'scroll' }}
                variant='scrollable'
                scrollButtons
                allowScrollButtonsMobile
                aria-label={t('common:tabs')}
                TabScrollButtonProps={{ style: { display: 'none' } }}
              >
                <Tab
                  label={t('common:details')}
                  value={LOCATION_DETAILS_PATH}
                  to={`${tabPath}${LOCATION_DETAILS_PATH}`}
                  component={Link}
                />
                <Tab
                  label={t('common:configuration')}
                  value={LOCATION_CONFIG_PATH}
                  to={`${tabPath}${LOCATION_CONFIG_PATH}`}
                  component={Link}
                  disabled={isConfigTabDisabled}
                />
              </TabList>
            </Box>
          </div>
        }
      >
        <LocationFormContext.Provider
          value={{
            dirty,
            errors,
            values: values,
            setDirty: setContextDirty,
            setError: setContextErrors,
            setValues,
            disabled: !hasPrivilege,
            getGeometricConfigEnabled,
          }}
        >
          <div className={classes.content}>
            <TabPanel className={classes.fullHeight} value={LOCATION_DETAILS_PATH}>
              <LocationDetails />
            </TabPanel>
            <TabPanel className={classes.fullHeight} value={LOCATION_CONFIG_PATH}>
              <Configuration tabPath={tabPath} locationParams={locationParams} />
            </TabPanel>
          </div>
          <RouteLeavingGuard ignoreBasePath={tabPath} when={isDirty} ignoreHash />
        </LocationFormContext.Provider>
      </CardPage>
    </TabContext>
  );
};

export default LocationForm;
